/**
 * Month helpers for the customer browse.
 *
 * Both customer tabs are ALWAYS bounded: either by a search term, or by one month of newly
 * created customers. An established workshop has thousands of customers and loading them
 * all is a slow page nobody reads — the server enforces this too and rejects an unbounded
 * browse, so the two must agree on what "a month" means.
 *
 * The month is carried as a plain `YYYY-MM` string rather than an offset from now. That is
 * what `<input type="month">` reads and writes, so staff can jump straight to a month
 * instead of clicking Previous repeatedly — and it stays correct if the page is left open
 * across midnight on the 1st, where an offset would silently start meaning a different
 * month than the one on screen.
 */

const pad = (n) => String(n).padStart(2, '0');

/** `YYYY-MM` for the current month — the default view. */
export const currentMonthValue = () => {
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
};

/**
 * Moves a `YYYY-MM` value by whole months.
 * Day 1 is used so the Date constructor rolls year boundaries for us.
 */
export const shiftMonth = (value, delta) => {
    const [y, m] = value.split('-').map(Number);
    const d = new Date(y, (m - 1) + delta, 1);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

/**
 * `YYYY-MM` → the ISO date bounds and a display label.
 *
 * Falls back to the current month on anything unparseable: a month input can be cleared,
 * and sending `undefined` to the API would fail server validation with a message about
 * date ranges that means nothing to whoever just emptied a box.
 */
export const rangeFromMonth = (value) => {
    const safe = /^\d{4}-\d{2}$/.test(value ?? '') ? value : currentMonthValue();
    const [y, m] = safe.split('-').map(Number);
    // Day 0 of the NEXT month is the last day of this one — handles February and leap
    // years without a table of month lengths.
    const lastDay = new Date(y, m, 0).getDate();
    return {
        value: safe,
        from: `${safe}-01`,
        to: `${safe}-${pad(lastDay)}`,
        label: new Date(y, m - 1, 1)
            .toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    };
};

export default rangeFromMonth;
